import { notification } from 'antd';
import React, { useEffect, useState } from 'react';

import { useModalForm } from '../../../../foundation/hooks';
import {
  displayUnAuthorizedNotification,
  somethingWentWrongNotification,
} from '../../../../foundation/utils/notifications';
import { LabelValuePair } from '../../../../foundation/utils/types';
import {
  useCreateVariantInBackgroundMutation,
  useLazyGetGeneSymbolsQuery,
  useLazyGetTranscriptsByGeneQuery,
  useLazyGetVariantMetadataQuery,
} from '../../../api/variantCheckApi';
import { VARIANT_STAGE_TYPES } from '../../../variant_workflow/constants';
import { VariantTranscript } from '../../../variant_workflow/redux';
import { VARIANT_CHECK_NOTIFICATION_MESSAGES } from '../../constants';
import { Variant } from '../../redux/types';

interface FormValues {
  gene_id: string;
  gene_symbol: string;
  transcript_primaryid: string;
  cdna: string;
}

interface UseCreateVariantModalLogicProps {
  closeModal: () => void;
  setNewVariantMetaData: React.Dispatch<React.SetStateAction<Variant | null>>;
  setVariantMetaDataModalVisibility: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  searchedString: string | undefined;
  onVariantCreationStarted: () => void;
  setNewVariantInfo: React.Dispatch<
    React.SetStateAction<{
      transcript_primaryid: string;
      cdna: string;
    } | null>
  >;
}

/**
 * Custom hook that encapsulates all the business logic for CreateVariantModal.
 * Separates concerns by extracting state management, selectors, and handlers
 * from the UI component.
 */
export const useCreateVariantModalLogic = ({
  closeModal,
  setNewVariantMetaData,
  setNewVariantInfo,
  setVariantMetaDataModalVisibility,
  searchedString,
  onVariantCreationStarted,
}: UseCreateVariantModalLogicProps) => {
  // Lazy query hooks return a [trigger, result] tuple; the trigger lets us
  // fetch on demand (e.g. on form submit) instead of on mount
  const [fetchGeneSymbols] = useLazyGetGeneSymbolsQuery();
  const [fetchTranscriptsByGeneId] = useLazyGetTranscriptsByGeneQuery();
  const [fetchVariantMetaData] = useLazyGetVariantMetadataQuery();
  const [createVariantInBackground] = useCreateVariantInBackgroundMutation();

  const { isLoading, setIsLoading, user, handleModalClose, createModalOpts } =
    useModalForm({
      onClose: closeModal,
      stageType: VARIANT_STAGE_TYPES.CLINICAL_OBSERVATION,
    });

  const [transcripts, setTranscripts] = useState<VariantTranscript[]>([]);

  const [autoFills, setAutoFills] = useState<
    | {
        transcript: string | undefined;
        cdna: string | undefined;
        gene: string | undefined;
      }
    | undefined
  >(undefined);

  useEffect(() => {
    if (searchedString) {
      // Pattern 1: NM_001606.5:c.6612C>A (transcript:cDNA)
      const pattern1Match = searchedString.match(
        /^(NM_[0-9]+\.[0-9]+):(c\.[0-9]+[A-Z]>[A-Z])$/,
      );
      // Pattern 2: ABCA2:c.6612C>A (gene:cDNA)
      const pattern2Match = searchedString.match(
        /^([A-Za-z0-9]+):(c\.[0-9]+[A-Z]>[A-Z])$/,
      );

      if (pattern1Match) {
        setAutoFills({
          transcript: pattern1Match[1],
          cdna: pattern1Match[2],
          gene: undefined,
        });
      } else if (pattern2Match) {
        setAutoFills({
          transcript: undefined,
          cdna: pattern2Match[2],
          // gene: pattern2Match[1],
          gene: undefined,
        });
      }
    }
  }, [searchedString]);

  const modalOpts = {
    ...createModalOpts('Create new variant'),
  };

  const initialValues: FormValues = {
    gene_id: autoFills?.gene || '',
    transcript_primaryid: autoFills?.transcript || '',
    cdna: autoFills?.cdna || '',
    gene_symbol: '',
  };

  const handleFormSubmit = async (values: any) => {
    if (user) {
      try {
        setIsLoading(true);
        const variantMetaData = await fetchVariantMetaData({
          transcript_primaryid: values.transcript_primaryid,
          cdna: values.cdna,
        }).unwrap();

        // Persist initial info for creation; variant_type will be selected in metadata modal
        setNewVariantInfo({
          transcript_primaryid: values.transcript_primaryid,
          cdna: values.cdna,
        });

        setNewVariantMetaData({
          ...variantMetaData,
          transcript_external_id: values.transcript_primaryid,
        });
        setVariantMetaDataModalVisibility(true);
        closeModal();
      } catch (error) {
        somethingWentWrongNotification();
      } finally {
        setIsLoading(false);
      }
    } else {
      displayUnAuthorizedNotification();
    }
  };

  const handleDirectCreate = async (values: FormValues) => {
    if (!user) {
      displayUnAuthorizedNotification();
      return;
    }

    try {
      setIsLoading(true);
      await createVariantInBackground({
        transcript_primaryid: values.transcript_primaryid,
        cdna: values.cdna,
      }).unwrap();
      notification.info({
        message: VARIANT_CHECK_NOTIFICATION_MESSAGES.VARIANT_CREATION_STARTED,
        description:
          VARIANT_CHECK_NOTIFICATION_MESSAGES.VARIANT_CREATION_STARTED_DESCRIPTION,
      });
      closeModal();
      onVariantCreationStarted();
    } catch {
      somethingWentWrongNotification();
    } finally {
      setIsLoading(false);
    }
  };

  const fetchGenes = (name: string): Promise<LabelValuePair[]> => {
    if (!user) {
      return Promise.resolve([]);
    }

    return fetchGeneSymbols(name)
      .unwrap()
      .then((genes) => {
        const xyz: LabelValuePair[] = genes
          ? genes.map((item: { gene_symbol: string; id: string | number }) => ({
              label: item.gene_symbol,
              value: String(item.id),
            }))
          : [];
        return xyz;
      })
      .catch((error) => {
        console.log(error);
        return [];
      });
  };

  const fetchTranscriptsForGene = async (geneId: string) => {
    try {
      setIsLoading(true);
      const transcripts: VariantTranscript[] =
        await fetchTranscriptsByGeneId(geneId).unwrap();

      setTranscripts(transcripts);
      setIsLoading(false);
      return transcripts;
    } catch (error) {
      setIsLoading(false);
      console.error('Error fetching transcripts:', error);
      return [];
    }
  };

  const handleGeneChange = (
    setFieldValue: (field: string, value: any) => void,
  ) => {
    return async (geneId: string, option?: LabelValuePair) => {
      setFieldValue('gene_id', geneId);
      setFieldValue('gene_symbol', option?.label ?? '');
      // Reset transcript when gene changes
      setFieldValue('transcript_primaryid', undefined);

      const fetchedTranscripts = await fetchTranscriptsForGene(geneId);

      // Auto-select MANE transcript if available
      const maneTranscript = fetchedTranscripts.find((t) => t.is_mane_select);
      if (maneTranscript) {
        setFieldValue(
          'transcript_primaryid',
          maneTranscript.transcript_external_id,
        );
      }
    };
  };

  return {
    isLoading,
    transcripts,
    autoFills,
    user,
    modalOpts,
    initialValues,
    handleModalClose,
    handleFormSubmit,
    handleDirectCreate,
    fetchGenes,
    handleGeneChange,
  };
};

export default useCreateVariantModalLogic;
